import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import RegistrationHeader from './components/RegistrationHeader';
import RegistrationForm from './components/RegistrationForm';
import SocialRegistration from './components/SocialRegistration';
import LocationPermissionModal from './components/LocationPermissionModal';
import RegistrationSuccess from './components/RegistrationSuccess';

const UserRegistration = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [registrationStep, setRegistrationStep] = useState('form'); // 'form', 'location', 'success'
  const [registeredUser, setRegisteredUser] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Mock user data for demonstration
  const mockUsers = [
    {
      email: "admin@servicewatch.com",
      password: "Admin123!",
      firstName: "Admin",
      lastName: "User"
    },
    {
      email: "user@example.com",
      password: "User123!",
      firstName: "Test",
      lastName: "User"
    }
  ];

  const handleRegistration = async (formData) => {
    setIsLoading(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Check if email already exists (mock validation)
      const existingUser = mockUsers?.find(user => user?.email === formData?.email);
      if (existingUser) {
        throw new Error('An account with this email already exists');
      }

      // Mock successful registration
      const newUser = {
        id: Date.now(),
        email: formData?.email,
        firstName: formData?.firstName,
        lastName: formData?.lastName,
        location: formData?.location,
        allowNotifications: formData?.allowNotifications,
        createdAt: new Date()?.toISOString(),
        verified: false
      };

      setRegisteredUser(newUser);
      setRegistrationStep('location');
      setShowLocationModal(true);

    } catch (error) {
      console.error('Registration error:', error);
      alert(error?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialRegistration = async (providerId) => {
    setIsLoading(true);

    try {
      // Simulate social registration delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Mock social registration success
      const socialUser = {
        id: Date.now(),
        email: `user@${providerId}.com`,
        firstName: 'Social',
        lastName: 'User',
        provider: providerId,
        createdAt: new Date()?.toISOString(),
        verified: true
      };

      setRegisteredUser(socialUser);
      setRegistrationStep('location');
      setShowLocationModal(true);

    } catch (error) {
      console.error('Social registration error:', error);
      alert('Social registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocationAllow = (location) => {
    // Save location data
    const updatedUser = {
      ...registeredUser,
      location: {
        latitude: location?.latitude,
        longitude: location?.longitude,
        accuracy: location?.accuracy,
        timestamp: new Date()?.toISOString()
      }
    };
    
    setRegisteredUser(updatedUser);
    setShowLocationModal(false);
    setRegistrationStep('success');
  };

  const handleLocationSkip = () => {
    setShowLocationModal(false);
    setRegistrationStep('success');
  };

  const handleContinueToDashboard = () => {
    // Store user session (in real app, this would be handled by auth system)
    localStorage.setItem('servicewatch_user', JSON.stringify(registeredUser));
    navigate('/outage-dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8 lg:py-12">
          <div className="max-w-2xl mx-auto">
            {registrationStep === 'form' && (
              <>
                <RegistrationHeader />
                
                <div className="bg-card border border-border rounded-xl shadow-sm p-6 lg:p-8">
                  <RegistrationForm
                    onSubmit={handleRegistration}
                    isLoading={isLoading}
                  />
                  
                  <div className="mt-8">
                    <SocialRegistration
                      onSocialRegister={handleSocialRegistration}
                      isLoading={isLoading}
                    />
                  </div>
                </div>

                {/* Trust Signals */}
                <div className="mt-8 text-center">
                  <div className="flex items-center justify-center space-x-6 text-xs text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                      <span>SSL Secured</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                      <span>Privacy Protected</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                      <span>GDPR Compliant</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {registrationStep === 'success' && (
              <div className="bg-card border border-border rounded-xl shadow-sm p-6 lg:p-8">
                <RegistrationSuccess
                  userEmail={registeredUser?.email}
                  onContinue={handleContinueToDashboard}
                />
              </div>
            )}
          </div>
        </div>
      </main>
      {/* Location Permission Modal */}
      <LocationPermissionModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onAllow={handleLocationAllow}
        onSkip={handleLocationSkip}
      />
      {/* Footer */}
      <footer className="bg-muted/30 border-t border-border mt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">
                Terms of Service
              </a>
              <span>•</span>
              <a href="#" className="hover:text-foreground transition-colors">
                Privacy Policy
              </a>
              <span>•</span>
              <a href="#" className="hover:text-foreground transition-colors">
                Help Center
              </a>
            </div>
            <div className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} ServiceWatch. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserRegistration;